import { UiToastType } from 'src/components/UI/toasts';
import { SignInRequestType, SigninResponseType } from 'src/models/Account/Signin';
import { SignupResponseType } from 'src/models/Account/Signup';
import { ResponseUserType } from 'src/models/Account/User';
import { RecaptchaViewModelType } from 'src/models/common';
import { InitialCommonStateType } from 'src/store/commonPart/slice';
import { PlusSubscriptionSliceStateType } from 'src/store/plusSubscription/slice';
import { PubNubMessage } from 'src/utils/PubNub';
import { SafeAnyType } from 'src/utils/safeAny';
import { tryCatch } from 'src/utils/tryCatch';

declare global {
  interface Window {
    dataLayer: {
      push: (event: SafeAnyType) => void;
    };

    EventAggregator: {
      publish: (event: string, data: SafeAnyType) => void;
      subscribe: (channel: string, callback: (id: string, message: PubNubMessage) => void) => number | string;
      unsubscribe: (token: string) => void;
    };

    RallyUp: {
      ChameleonManager: {
        TrackEvent: (s: string) => void;
      };
      UserPilotManager: {
        TrackEvent: (s: string) => void;
      };
      PubNub: {
        JoinChannel: (channel: string) => unknown;
        LeaveChannel: (channel: string) => unknown;
      };
      GoogleAnalyticsManager: {
        SendPurchaseGoogleEvent: (eventData: SafeAnyType) => void;
        Init: (options: SafeAnyType) => void;
      };
      FacebookPixelManager: {
        TrackPartnerEvent: (type: string) => void;
        TrackPurchaseEvent: (eventData: SafeAnyType) => void;
        Init: (options: SafeAnyType) => void;
      };
      ChatWidgetManager: {
        Open: () => void;
        SwitchToHubSpot: () => void;
      };
      SocialLinksController: {
        Facebook: (s: string) => void;
        Twitter: (s: string) => void;
        LinkedIn: (s: string) => void;
        Link: (s: string) => void;
      };
      SocialLinksController_26794: {
        Facebook: (s: string) => void;
        Twitter: (s: string) => void;
        LinkedIn: (s: string) => void;
        Link: (
          s: string,
          t?: string | null,
          ttt?: string | null,
          tttt?: string | null,
          i?: boolean,
          tt?: string | null
        ) => void;
        Email: (subject?: string, body?: string) => void;
        Mobile: (s: string, title?: string, text?: string) => void;
      };
      AIWidgetManager: {
        Toggle: () => void;
        IsAvailable: () => boolean;
      };
    };

    FS?: {
      anonymize: () => void;
      identify: (uuid: string, props: Record<string, unknown>) => void;
    };

    spa: {
      signin: (data: SignInRequestType, callback: (response: SigninResponseType | null) => void) => void;
      signout: (callback: (response: string | null) => void) => void;
      signup: (
        data: {
          email: string;
          firstName: string;
          lastName: string;
          password: string;
          confirmPassword: string;
          marketingOptIn: boolean;
          recaptchaToken: string;
          RecaptchaViewModel: RecaptchaViewModelType;
        },
        callback: (response: SignupResponseType | null) => void
      ) => void;
      /** call this after signout from any part of the app */
      onSignout: () => void;
      showLoginModal: (show: boolean, permanentUrl?: string) => void;
      showTemplateGalleryModal: (calledFrom?: InitialCommonStateType['templateGalleryModalCalledFrom']) => void;

      /* plus subscription */
      showExclusiveModal: (type: NonNullable<PlusSubscriptionSliceStateType['modal']['type']>) => void;
      showPlanSelectionModal: () => void;
      showUpgradeNowModal: () => void;
      showDonorProfileOverlay: (id: number) => void;
      showCampaignOverlay: (id: number, noSearchParam?: boolean) => void;
      uploadFile: (
        body: File | Blob,
        mime: string,
        fileExtension: string,
        fileNamePrefix: string,
        successCallback: (filePath: string) => void,
        errorCallback: (response: { originalErrorMsg: string; message: string }) => void
      ) => void;
    };

    toast: {
      success: UiToastType;
      error: UiToastType;
      info: UiToastType;
    };

    uetq: SafeAnyType;
  }
}

/** notify knockout when a user changes */
export const eventPublishAuthChanged = (data: ResponseUserType | null): void => {
  tryCatch(() => {
    window.EventAggregator.publish('Authentication_Changed', data);
  });
};

export const eventPublishShowPlusSubscriptionTrialBanner = (data: ResponseUserType | null): void => {
  tryCatch(() => {
    window.EventAggregator.publish('ShowPlusSubscriptionTrialBanner', data);
  });
};

export const openChatWidget = (): void => {
  tryCatch(() => {
    window.RallyUp.ChatWidgetManager.Open();
  });
};

export const switchToHubSpot = (): void => {
  tryCatch(() => {
    window.RallyUp.ChatWidgetManager.SwitchToHubSpot();
  });
};

/** TODO: delete after adding the chat manager icon component */
export const toggleChatWidgetIcon = (show: boolean): void => {
  tryCatch(() => {
    if (show) {
      document.body.classList.remove('hide-chat-widget');
    } else {
      document.body.classList.add('hide-chat-widget');
    }
  });
};
